import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxBadgeComponent,
  OnyxLinkComponent,
  type OnyxColor,
} from '@onyx/angular';
import { AmountCellComponent } from '../../../../common/components/cells/amount-cell/amount-cell.component';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { InvoiceType } from '../../../../common/enums/invoice-type';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import { ContractorHelper } from '../../../management-panel/contractors/common/helpers/contractor.helper';
import { OrderStatus } from '../../common/enums/order-status';
import { OrderHelper } from '../../common/helpers/order.helper';
import type { Order } from '../../common/interfaces/order';
import { OrderModalStagesComponent } from '../order-modal-stages/order-modal-stages.component';

@Component({
  selector: 'app-order-modal-header-description',
  imports: [
    OnyxLinkComponent,
    AmountCellComponent,
    OrderModalStagesComponent,
    OnyxBadgeComponent,
    TranslatePipe,
  ],
  templateUrl: './order-modal-header-description.component.html',
  styleUrl: './order-modal-header-description.component.scss',
})
export class OrderModalHeaderDescriptionComponent {
  protected readonly contractorHelper = inject(ContractorHelper);
  private readonly orderHelper = inject(OrderHelper);
  private readonly commonHelper = inject(CommonHelper);

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.orderModal';

  protected isOrderInactive = computed(
    () => this.order() && OrderHelper.isInactive(this.order()!),
  );

  protected missingAssignment = computed(() =>
    OrderHelper.getMissingAssignment(this.order()),
  );
  protected isInProgress = computed(
    () => this.order().status.value === OrderStatus.IN_PROGRESS,
  );
  protected currentPoint = computed(() =>
    OrderHelper.getCurrentPoint(this.order().points),
  );
  protected pointData = computed(() =>
    RouteHelper.getPointRouteData(
      this.currentPoint(),
      this.order().points,
      this.commonHelper.nowMinute(),
    ),
  );
  protected statusName = computed(() => {
    const missingAssignment = this.missingAssignment();
    if (missingAssignment) return missingAssignment.label;

    const status = this.order().status.value;
    const statusLabel = `${DictionaryCode.ORDER_STATUS}.${status}`;

    switch (status) {
      case OrderStatus.IN_PROGRESS:
        return 'labels.inProgress';
      case OrderStatus.VEHICLE_SEARCH:
        return 'labels.unassignedVehicle';
      case OrderStatus.INVOICE_TO_ISSUE: {
        return {
          [InvoiceType.COLLECTIVE]: `${this.I18N}.waitingForCollectiveInvoice`,
          [InvoiceType.SELF_BILLING]: `${this.I18N}.waitingForSelfBilling`,
          [InvoiceType.SINGLE]: statusLabel,
        }[this.order().payment.contractorInvoice.defaultType];
      }
      case OrderStatus.CANCELED:
        return 'labels.canceled';
      default: {
        if (status === OrderStatus.TO_DO && this.order().outsourcingData) {
          return 'labels.toOutsourcing';
        }

        return statusLabel;
      }
    }
  });
  protected statusColor = computed(() => {
    if (this.missingAssignment()) {
      return this.order().relatedFleetSwap ? 'light-orange' : 'light-red';
    }

    const { status } = this.order();
    const color = `light-${status.color}` as OnyxColor;
    const pointComplianceColor = this.pointData()?.complianceColor;
    const complianceColor = pointComplianceColor
      ? (`light-${pointComplianceColor}` as OnyxColor)
      : null;

    switch (status.value) {
      case OrderStatus.DRAFT:
        return 'light-gray';

      case OrderStatus.VEHICLE_SEARCH: {
        const deadlineTime = this.pointData()?.deadlineTime;
        if (!deadlineTime) return 'light-blue';

        return CommonHelper.isPast(deadlineTime) ? 'light-red' : 'light-blue';
      }

      case OrderStatus.TO_DO: {
        if (this.order().outsourcingData) return 'light-sea-green';
        return complianceColor ?? color;
      }

      case OrderStatus.WAITING_FOR_PAYMENT:
      case OrderStatus.PARTIALLY_PAID:
        return this.orderHelper.getPaymentDelay(this.order())?.isPast
          ? 'light-red'
          : color;

      default: {
        if (OrderHelper.isFinished(status.value)) return color;

        if (
          this.isInProgress() &&
          !this.pointData()?.estimations &&
          !this.pointData()?.deadlineTime
        ) {
          return 'light-gray';
        }

        return complianceColor ?? color;
      }
    }
  });
}
